import { ChevronDown } from "lucide-react";

import { cn } from "~/lib/utils";
import { Button } from "./button";
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuTrigger } from "./dropdown-menu";

interface MultiSelectOption<T extends string | number> {
  value: T;
  label: string;
}

interface MultiSelectProps<T extends string | number> {
  label: string;
  options: ReadonlyArray<MultiSelectOption<T>>;
  selected: T[];
  onChange: (next: T[]) => void;
  className?: string;
}

/**
 * Checkbox-style multi-select built on the shadcn DropdownMenu. Replaces V1's
 * MUI multiple `<Select>` (segment / group / role pickers and list filters).
 */
export function MultiSelect<T extends string | number>({
  label,
  options,
  selected,
  onChange,
  className,
}: MultiSelectProps<T>) {
  const selectedLabels = options.filter((o) => selected.includes(o.value)).map((o) => o.label);
  const toggle = (value: T) =>
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn("h-9 min-w-[160px] justify-between font-normal", className)}
        >
          <span className={cn("truncate", selectedLabels.length === 0 && "text-fg-3")}>
            {selectedLabels.length > 0 ? selectedLabels.join(" / ") : label}
          </span>
          <ChevronDown className="ml-1 shrink-0 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-[320px] min-w-[200px] overflow-y-auto">
        {options.map((option) => (
          <DropdownMenuCheckboxItem
            key={option.value}
            checked={selected.includes(option.value)}
            onCheckedChange={() => toggle(option.value)}
            onSelect={(e) => e.preventDefault()}
          >
            {option.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
